import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { InputWithDropdown } from '@/components/ui/input-with-dropdown';
import { useDebounce } from '@/hooks/useDebounce';

interface TotalOrdersFilterProps {
  onChange: (lowerLimit: number | null, upperLimit: number | null) => void;
  lowerLimit: number | null;
  upperLimit: number | null;
}

const TotalOrdersFilter: React.FC<TotalOrdersFilterProps> = ({
  onChange,
  lowerLimit = 1,
  upperLimit = null
}) => {
  const [localLowerLimit, setLocalLowerLimit] = useState<number>(lowerLimit || 1);
  const [localUpperLimit, setLocalUpperLimit] = useState<number | null>(upperLimit);
  const [lowerInputValue, setLowerInputValue] = useState<string>((lowerLimit || 1).toString());
  const [upperInputValue, setUpperInputValue] = useState<string>(upperLimit ? upperLimit.toString() : '');
  
  // Common order count options for quick selection
  const commonOrderCounts = [1, 2, 3, 5, 10, 15, 20, 25, 30, 40, 50, 75, 100];
  
  // Debounce the values to prevent too many API calls
  const debouncedLowerLimit = useDebounce(localLowerLimit, 500);
  const debouncedUpperLimit = useDebounce(localUpperLimit, 500);

  // Update local state when props change
  useEffect(() => {
    setLocalLowerLimit(lowerLimit || 1);
    setLocalUpperLimit(upperLimit);
    setLowerInputValue((lowerLimit || 1).toString());
    setUpperInputValue(upperLimit ? upperLimit.toString() : '');
  }, [lowerLimit, upperLimit]);
  
  // Call onChange when debounced values change
  useEffect(() => {
    onChange(debouncedLowerLimit, debouncedUpperLimit);
  }, [debouncedLowerLimit, debouncedUpperLimit, onChange]);

  // Generate options for dropdowns
  const lowerOptions = commonOrderCounts.map(count => ({
    value: count.toString(),
    label: count.toString()
  }));
  
  const upperOptions = [
    { value: 'any', label: 'Any' },
    ...commonOrderCounts
      .filter(count => count > localLowerLimit)
      .map(count => ({
        value: count.toString(),
        label: count.toString()
      }))
  ];
  
  const handleLowerInputChange = (value: string) => {
    setLowerInputValue(value);
    
    if (value === '') {
      setLocalLowerLimit(1);
      setLowerInputValue('1');
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue >= 1) {
        setLocalLowerLimit(numValue);
        
        // If upper limit is set and lower than new lower limit, clear it
        if (localUpperLimit !== null && localUpperLimit <= numValue) {
          setLocalUpperLimit(null);
          setUpperInputValue('');
        }
      }
    }
  };

  const handleUpperInputChange = (value: string) => {
    setUpperInputValue(value);
    
    if (value === '' || value.toLowerCase() === 'any') {
      setLocalUpperLimit(null);
      setUpperInputValue('');
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue > localLowerLimit) {
        setLocalUpperLimit(numValue);
      }
    }
  };
  
  const handleLowerInputBlur = () => {
    if (lowerInputValue === '' || isNaN(parseInt(lowerInputValue)) || parseInt(lowerInputValue) < 1) {
      setLowerInputValue('1');
      setLocalLowerLimit(1);
    }
  };
  
  const handleUpperInputBlur = () => {
    if (upperInputValue !== '' && !isNaN(parseInt(upperInputValue))) {
      const value = parseInt(upperInputValue);
      if (value <= localLowerLimit) {
        setUpperInputValue('');
        setLocalUpperLimit(null);
      }
    }
  };

  return (
    <div className="space-y-3">
      <Label className="text-base">Total Orders Placed</Label>
      <div className="flex flex-col md:flex-row gap-4 w-full">
        <div className="space-y-2 flex-1">
          <Label htmlFor="min-orders">Minimum Orders</Label>
          <InputWithDropdown
            id="min-orders"
            type="number"
            min={1}
            value={lowerInputValue}
            onChange={handleLowerInputChange}
            onBlur={handleLowerInputBlur}
            options={lowerOptions}
            placeholder="Enter or select"
          />
        </div>

        <div className="space-y-2 flex-1">
          <Label htmlFor="max-orders">Maximum Orders</Label>
          <InputWithDropdown
            id="max-orders"
            type="text"
            value={upperInputValue}
            onChange={handleUpperInputChange}
            onBlur={handleUpperInputBlur}
            options={upperOptions}
            placeholder="Any"
          />
        </div>
      </div>
    </div>
  );
};

export default TotalOrdersFilter;